/**
 * Feature flags — single source of truth for surfaces that are hidden from
 * users until they're ready to ship.
 *
 * Default values in FEATURES are the production defaults. Any flag can be
 * overridden at build time by setting `VITE_FF_<KEY>=true|false` in the
 * shell or in a `.env.local` file — useful for working on a hidden surface
 * locally without touching the committed defaults.
 *
 * A flag that's off means:
 *   - The nav item is filtered out of the Sidebar (and any nav group that
 *     ends up with zero visible items is filtered out too).
 *   - The route is not registered in App.tsx, so a direct URL falls
 *     through to the NotFound handler instead of rendering a half-built
 *     page.
 */

export const FEATURES = {
  // AI Systems group — the whole group is pre-release.
  plans: false,
  swarms: false,
  skills: false,
  costs: false,
  memory: false,
  sessions: false,

  // System group — admin / ops surfaces that aren't polished for general use.
  operations: false,
  admin: false,
  guardrails: false,
  agentSettings: false,

  // Pipeline — new surfaces still in early rollout.
  buyerGen: true,

  // Scout config — county/equity/schedule knobs. Hidden until the
  // Scout autonomous loop is wired (today these settings persist but
  // don't actually drive agent behavior).
  // (Note: per-agent permission editing is already gated by
  // `agentSettings` above — same reason, different surface.)
  scoutConfig: false,

  // ── Out-of-scope for the core production loop ──
  // These exist as built-out pages but aren't part of the eight surfaces
  // we're committing to make excellent. Hidden from the default sidebar
  // until the core loop is production-ready. Direct URLs still work for
  // power users / admins who want them, gated via a VITE_FF env override.
  toolStore: false,
  parcelGen: false,
  parcelData: false,
  research: false,
} as const

export type FeatureFlag = keyof typeof FEATURES

/**
 * Resolution order (highest precedence first):
 *   1. localStorage override (set by admin from /admin → Feature Flags)
 *   2. VITE_FF env var (build-time per-environment toggle)
 *   3. Bundled default in FEATURES above
 */
export function isEnabled(flag: FeatureFlag): boolean {
  if (typeof window !== 'undefined') {
    const stored = window.localStorage.getItem(`forge-ff-${flag}`)
    if (stored === 'true') return true
    if (stored === 'false') return false
  }
  const envVal = import.meta.env[`VITE_FF_${flag.toUpperCase()}`] as string | undefined
  if (envVal === 'true') return true
  if (envVal === 'false') return false
  return FEATURES[flag]
}
